import {
  Box,
  DataGrid,
  ErrorBox,
  Flex,
  NoRowsOverlay,
  SearchInput,
  type GridSortingInitialState,
  type GridSortModel,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { OperatorType, OrderType, useSearchUserAppRoleViewQuery } from '@user-management/graphql';
import { APP_PERMISSIONS } from '@user-management/permissions';
import { useRole } from '@user-management/role-management';
import { useMemo, type ChangeEvent } from 'react';

import { useRoleUserListSearchParams } from '../../hooks';

import { baseColumns, deleteUserColumn } from './columns';
import { mapAppRolesUserList } from './mapper';

function getFieldName(field: string) {
  if (field === 'profile') return 'firstname';
  return field;
}

export default function RoleUserList() {
  const { appRoleData } = useRole();
  const { id: appRoleId } = appRoleData;

  const hasUpdateRolePermission = useHasPermission([
    APP_PERMISSIONS.SETTING.ROLE.UPDATE,
    APP_PERMISSIONS.SETTING.USER.UPDATE,
  ]);

  const columns = hasUpdateRolePermission ? baseColumns.concat(deleteUserColumn) : baseColumns;

  const [
    { field, sort, page, pageSize, search },
    { changePageNumber, changePageSize, changeSortingField, changeSearch },
  ] = useRoleUserListSearchParams();

  const {
    data,
    loading: isLoading,
    error,
  } = useSearchUserAppRoleViewQuery({
    variables: {
      order: {
        field: field,
        type: sort,
      },
      paging: {
        pageSize: pageSize,
        currentPage: page,
      },
      appRoleId: appRoleId,
      search: {
        value: search ?? '',
        operator: OperatorType.Or,
      },
      invertRoleFilter: false,
    },
  });

  const rows = useMemo(() => {
    if (data?.getUserAppRoleView) {
      return mapAppRolesUserList(data?.getUserAppRoleView?.users);
    }
    return [];
  }, [data]);

  const handlePageChange = (page: number) => {
    changePageNumber(page);
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    changePageSize(parseInt(event.target.value));
    changePageNumber(0);
  };

  const handleSortModelChange = (sorting: GridSortModel) => {
    const field = sorting[0]?.field ?? 'profile';
    const sort = (sorting[0]?.sort?.toUpperCase() ?? OrderType.Asc) as OrderType;
    changeSortingField(getFieldName(field), sort);
  };

  const handleSearch = (value: string) => {
    changeSearch(value);
    changePageNumber(0);
  };

  if (error) {
    return <ErrorBox />;
  }

  const totalCount = data?.getUserAppRoleView?.total ?? 0;
  const shouldHideFooter = isLoading || totalCount === 0;

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: (theme) => theme.spacing(6),
      }}
    >
      <Box sx={{ width: '318px' }}>
        <SearchInput placeholder='ค้นหาผู้ใช้...' onChange={handleSearch} />
        {/* TODO: add filter by status section, after api is support for this filter */}
      </Box>
      <DataGrid
        columns={columns}
        rows={rows}
        loading={isLoading}
        hideFooter={shouldHideFooter}
        disableColumnMenu
        getRowId={(row) => row.userId}
        slots={{ noRowsOverlay: NoRowsOverlay }}
        slotProps={{
          footer: {
            total: totalCount,
            pagination: {
              page: page,
              count: totalCount,
              rowsPerPage: pageSize,
              onPageChange: (_event, page) => {
                handlePageChange(page);
              },
              onRowsPerPageChange: handleRowsPerPageChange,
            },
          },
        }}
        initialState={{
          sorting: {
            sortModel: field
              ? [{ field: field === 'firstname' ? 'profile' : field, sort: sort.toLowerCase() }]
              : undefined,
          } as GridSortingInitialState,
        }}
        onSortModelChange={handleSortModelChange}
        sx={(theme) => ({
          backgroundColor: theme.palette.common.white,
          '--DataGrid-overlayHeight': '412px',
          '& .MuiDataGrid-cell': {
            padding: theme.spacing(3),
          },
          '.MuiDataGrid-columnHeaders': {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          },
          '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          },
        })}
      />
    </Flex>
  );
}
